import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { ToggleButtonObjectType } from '@niceltd/cxone-components/toggle-button-group/src/lib/toggle-button-group.component';
import Highcharts from 'highcharts';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { PopoutContext } from '../../toolbar-items/cxone-popout/cxone-popout.component';
import { FrustrationModeSelectorComponent } from '../../toolbar-items/frustration-mode-selector/frustration-mode-selector.component';
import { IAFrustrationDataModel } from './ia-frustration.models';
import { IAFrustrationService } from './ia-frustration.service';

@Component({
    selector: 'cv-ia-frustration',
    templateUrl: './ia-frustration.component.html',
    styleUrls: ['./ia-frustration.component.scss']
})
export class IaFrustrationComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild("chartCanvas") chartCanvas: ElementRef<HTMLElement>;

    abortLoad: () => void = null;

    public chartInstance: Highcharts.Chart;
    public highcharts = Highcharts;
    public noData = false;

    private highchartsConfig: Highcharts.Options = null;

    private isFirstLoad = true;
    private isAborting = false;
    private data: Array<IAFrustrationDataModel> = null;
    private dataLoading = false;
    private dataLoadPromise = null;

    private popoutContext: PopoutContext = {
        url: () => this.getPopoutUrl(),
        // isVisible: () => !this.noDatasetSelected
        isVisible: () => true // currently always visible
    };
    private frustrationModeContext = {
        IsDataLoading: () => this.IsDataLoading,
        onViewChange: () => this.onViewChange.bind(this),
        initialViewButton: () => this.initialViewButton,
        viewButtons: () => this.viewButtons,
        viewStateLoaded: () => this.viewStateLoaded,
        shouldBeHidden: () => this.noDatasetSelected,
    };

    private selectedView = 'Categories';
    private _viewStateLoaded = false;
    private _viewButtons: Array<ToggleButtonObjectType> = null;

    constructor(
        private service: IAFrustrationService,
        private cvLocalizationService: CVLocalizationService,
        private navigationService: NavigationService,
    ) {
        if (Highcharts.AST.allowedTags.indexOf("use") === -1)
            Highcharts.AST.allowedTags.push("use");

        if (Highcharts.AST.allowedAttributes.indexOf("href") === -1)
            Highcharts.AST.allowedAttributes.push("href");
    }

    public get viewButtons() {
        if (this._viewButtons == null)
            this.createViewButtons();
        return this._viewButtons;
    }

    public get initialViewButton() {
        return this.viewButtons.find(b => b.id === `frustration_${this.moduleKey}_${this.selectedView.toLowerCase()}`);
    }

    public get viewStateLoaded() { return this._viewStateLoaded; }
    public get noDatasetSelected() { return this.moduleManager.customSettings.frustrationDataset?.getValue() == null; }
    public get IsDataLoading() { return this.dataLoading; }
    public get chartOptions() { return this.highchartsConfig; }


    @HostListener('window:resize', ['$event'])
    onResize(event) {
        setTimeout(() => this?.chartInstance?.reflow(), 500);
    }

    setChartInstance(chart: Highcharts.Chart) {
        this.chartInstance = chart;
        chart?.reflow();
    }

    async onViewChange(button) {
        this.selectedView = button.id.indexOf('_agents') > -1 ? 'Agents' : 'Categories';
        //if (!this.isFirstLoad || !this.cxoneTogglesInitialized) { // CXone control hits this on control creation. Need to not load since the ngOnInit/ModuleManager.settingsApplied handles the first load.
        await this.moduleManager?.saveState();
        await this.loadData(false);
    }

    openSettings() {
        this.moduleManager?.container?.openSettings();
    }

    async ngOnInit(): Promise<void> {
        await this.cvLocalizationService.GetSection("Frustration_Widget");

        this.setModuleManagerOptions();

        this.moduleManager.addToolbarItem(FrustrationModeSelectorComponent, this.frustrationModeContext);
        // Removing until we have a good solution for case sensitive links
        //this.moduleManager.addToolbarItem(CxonePopoutComponent, this.popoutContext);
    }

    async loadData(isAutoRefresh) {
        if (this.dataLoadPromise) { // cancel current load
            this.isAborting = true;
            this.abortLoad?.();
            this.dataLoadPromise = null;
            this.abortLoad = null;
        }

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        this.dataLoading = true;
        const abort = new Promise<void>((resolve, reject) => this.abortLoad = () => resolve());
        this.dataLoadPromise = this.service.LoadFrustrationData(this.moduleKey, isAutoRefresh, this.isFirstLoad, this.selectedView, abort);

        try {
            this.data = await this.dataLoadPromise; // returns response.data

            if (this.data == null || this.data.length < 1) {
                this.noData = true;
            }
            else {
                this.noData = false;
            }


            this.dataLoading = false; // Need to set this first before creating the highcharts config as it checks if the data is still loading to cancel the chart creation
            if (this.selectedView === 'Categories')
                this.createHighchartsConfig_CategoryView();
            else this.createHighchartsConfig_AgentView();

        } catch (error) {
            if (this.isAborting) {
                // failed because we cancelled the request
                this.isAborting = false;
                return;
            }
            this.moduleManager.handleLoadError(error, isAutoRefresh);
        }

        this.moduleManager.showLoader = false;

        // Reset loader flags
        this.dataLoading = false;
        this.dataLoadPromise = null;
        this.isFirstLoad = false;
    };

    public getPopoutUrl(): string {
        const baseUrl = `${this.navigationService.cxoneTokenUrls?.analyticsBaseUrl ?? ''}`;
        const encodedDatasetName = encodeURI(this.moduleManager?.customSettings?.frustrationDataset?.getValue()?.DatasetName ?? "");
        if (encodedDatasetName === "")
            return "";
        return `${baseUrl}/app/#/app/workspace/${encodedDatasetName}`;
    }

    private createHighchartsConfig_CategoryView() {
        if (this.dataLoading)
            return;

        const component = this;
        const widgetData = this.data;

        this.highchartsConfig = {
            chart: {
                type: 'column',
            },
            xAxis: {
                categories: this.data.map(d => d.ByCategories.Label)
            },
            title: {
                text: undefined,
            },
            yAxis: {
                min: 0,
                stackLabels: {
                    enabled: true,
                    style: {
                        fontWeight: 'bold',
                        color: (Highcharts.defaultOptions.title.style && Highcharts.defaultOptions.title.style.color) || 'gray',
                        textOutline: 'none'
                    }
                }
            },
            legend: {
                align: 'right',
                verticalAlign: 'top',
                floating: false,
                backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || 'white',
                borderColor: '#CCC',
                borderWidth: 1,
                shadow: false
            },
            credits: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                shared: true,
                stickOnContact: true,
                // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
                formatter(tooltip) {
                    const rawData = widgetData.find(wd => wd?.ByCategories?.Label === this.x) || null;
                    const firstAgent = rawData?.ByCategories?.AgentBreakdown?.[0] || null;
                    const secondAgent = rawData?.ByCategories?.AgentBreakdown?.[1] || null;
                    const thirdAgent = rawData?.ByCategories?.AgentBreakdown?.[2] || null;
                    const mediaTypes = rawData?.ByCategories?.TooltipData || [];
                    let html = `
                                    <div class="frustration-tooltip" style="user-select:text; cursor: auto;">
                                        <div class="pb-2 pt-1" style="border-bottom: 1px solid #E0E2E4">
                                            <b>${this.x}</b>
                                        </div>
                                        <div class="d-flex">
                                            <div class="mb-1 mt-2 mr-2" style="flex-grow: 2;">
                                                <b>${component.cvLocalizationService.getCachedText(// eslint-disable-next-line indent
                                                    'Frustration_Widget', 'Top_3_Agents', 'Top 3 Agents')}:</b></br>
                                                ${firstAgent ? firstAgent.AgentCombinedName +
                                                    ': <b>' + firstAgent.FrustrationCount + '</b><br/>' : ''}
                                                ${secondAgent ? secondAgent.AgentCombinedName +
                                                    ': <b>' + secondAgent.FrustrationCount + '</b><br/>' : ''}
                                                ${thirdAgent ? thirdAgent.AgentCombinedName +
                                                    ': <b>' + thirdAgent.FrustrationCount + '</b><br/>' : ''}
                                            </div>
                                            <div class="d-flex flex-column my-1 flex-fill frustration-media-type-container">
                                        `;
                    for(const media of mediaTypes) {
                        html += `<span>
                                    <svg class="icon-20 m-1">
                                        <use href="../App_Themes/CXone/icons/${media.IconPath}"></use>
                                    </svg>
                                    <span>${(media.PercentToTotal * 100).toFixed(2)}%</span>
                                </span>`;
                    }
                    html += `                                                
                                </div>
                            </div>
                            <div class="pt-2" style="border-top: 1px solid #E0E2E4">
                                <b>${rawData?.ByCategories?.FrustrationPercent.toFixed(2)}% 
                                ${component.cvLocalizationService.getCachedText(// eslint-disable-next-line indent
                                    'Frustration_Widget', 'Frustration', 'Frustration')}</b>
                            </div>
                        </div>
                    `;
                    return html;
                }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    dataLabels: {
                        enabled: true
                    },
                    borderColor: "#2E2E2E26",
                }
            },
            series: [
                {
                    name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'Total_Interactions', 'Total Interactions'),
                    type: undefined,
                    data: this.data.map(d => d.ByCategories.NonfrustrationCount),
                    color: "#E9E9E980",
                    dataLabels: {
                        enabled: false
                    }
                },
                {
                    name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'Frustrations', 'Frustrations'),
                    type: undefined,
                    data: this.data.map(d => d.ByCategories.FrustrationCount),
                    color: "#F7D083",
                }
            ]
        };
    }

    private createHighchartsConfig_AgentView() {
        if (this.dataLoading)
            return;

        const component = this;
        const widgetData = this.data;

        this.highchartsConfig = {
            chart: {
                type: 'column',
            },
            xAxis: {
                categories: this.data.map(d => d.ByAgents.Label)
            },
            title: {
                text: undefined,
            },
            yAxis: {
                min: 0,
                stackLabels: {
                    enabled: true,
                    style: {
                        fontWeight: 'bold',
                        color: (Highcharts.defaultOptions.title.style && Highcharts.defaultOptions.title.style.color) || 'gray',
                        textOutline: 'none'
                    }
                }
            },
            legend: {
                align: 'right',
                verticalAlign: 'top',
                floating: false,
                backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || 'white',
                borderColor: '#CCC',
                borderWidth: 1,
                shadow: false
            },
            credits: {
                enabled: false
            },
            tooltip: {
                useHTML: true,
                shared: true,
                stickOnContact: true,
                // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
                formatter(tooltip) {
                    const rawData = widgetData.find(wd => wd?.ByAgents?.Label === this.x) || null;
                    const firstCategory = rawData?.ByAgents?.CategoryBreakdown?.[0] || null;
                    const secondCategory = rawData?.ByAgents?.CategoryBreakdown?.[1] || null;
                    const thirdCategory = rawData?.ByAgents?.CategoryBreakdown?.[2] || null;
                    const mediaTypes = rawData?.ByAgents?.TooltipData || [];
                    let html = `
                                    <div class="frustration-tooltip" style="user-select:text; cursor: auto;">
                                        <div class="pb-2 pt-1" style="border-bottom: 1px solid #E0E2E4">
                                            <b>${this.x}</b>
                                        </div>
                                        <div class="d-flex">
                                            <div class="mb-1 mt-2 mr-2" style="flex-grow: 2;">
                                                <b>${component.cvLocalizationService.getCachedText(// eslint-disable-next-line indent
                                                    'Frustration_Widget', 'Top_3_Categories', 'Top 3 categories')}:</b></br>
                                                ${firstCategory ? firstCategory.CategoryName +
                                                    ': <b>' + firstCategory.FrustrationCount + '</b><br/>' : ''}
                                                ${secondCategory ? secondCategory.CategoryName +
                                                    ': <b>' + secondCategory.FrustrationCount + '</b><br/>' : ''}
                                                ${thirdCategory ? thirdCategory.CategoryName +
                                                    ': <b>' + thirdCategory.FrustrationCount + '</b><br/>' : ''}
                                            </div>
                                            <div class="d-flex flex-column my-1 flex-fill frustration-media-type-container">
                                        `;
                    for(const media of mediaTypes) {
                        html += `<span>
                                    <svg class="icon-20 m-1" style="margin-right: 8px;">
                                        <use href="../App_Themes/CXone/icons/${media.IconPath}"></use>
                                    </svg>
                                    <span>${(media.PercentToTotal * 100).toFixed(2)}%</span>
                                </span>`;
                    }
                    html += `
                            </div>
                        </div>
                        <div class="pt-2" style="border-top: 1px solid #E0E2E4">
                            <b>${rawData?.ByAgents?.FrustrationPercent.toFixed(2)}%
                            ${component.cvLocalizationService.getCachedText(// eslint-disable-next-line indent
                                'Frustration_Widget', 'Frustration', 'Frustration')}</b>
                        </div>
                    </div>
                    `;
                    return html;
                }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                    dataLabels: {
                        enabled: true
                    },
                    borderColor: "#2E2E2E26",
                }
            },
            series: [
                {
                    name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'Total_Interactions', 'Total Interactions'),
                    type: undefined,
                    data: this.data.map(d => d.ByAgents.NonfrustrationCount),
                    color: "#E9E9E980",
                    dataLabels: {
                        enabled: false
                    }
                },
                {
                    name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'Frustrations', 'Frustrations'),
                    type: undefined,
                    data: this.data.map(d => d.ByAgents.FrustrationCount),
                    color: "#F7D083",
                }
            ]
        };
    }

    private setModuleManagerOptions() {
        this.moduleManager.setOptions({
            onSettingsApplied: async (settings) => {
                if (this.moduleManager.customSettings?.frustrationDataset?.getValue() != undefined) {
                    this.moduleManager.container.clearCustomCXoneError();
                    const baseUrl = `${this.navigationService.cxoneTokenUrls?.analyticsBaseUrl ?? ''}`;
                    const encodedDatasetName = encodeURI(this.moduleManager.customSettings.frustrationDataset.getValue().DatasetName);
                    //this.popoutContext.url = `${baseUrl}/app/#/app/workspace/${encodedDatasetName}`;

                    await this.loadData(false);
                }
                else {
                    this.moduleManager.container.setCustomCXoneError(
                        'Frustration_Widget', 'No_Data_Set',
                        'Frustration_Widget', 'Select_Data_Set',
                        () => this.openSettings(),
                        true,
                    );
                }
            },
            hasRefresh: true,
            refreshType: null, // means no auto refresh
            onRefresh: async isAutoRefresh => {
                if (this.moduleManager.customSettings?.frustrationDataset?.getValue() != undefined) {
                    await this.loadData(isAutoRefresh);
                }
            },
            canRefresh: () => !this.dataLoading,
            onExport: async (format, isCxOne) => await this.service.LoadFrustrationDataForExport(
                this.moduleKey, format, this.selectedView, isCxOne)
        });

        this.moduleManager.registerEvents({
            onResize: () => this.chartInstance?.reflow(),
            onResizeStop: () => window.setTimeout(() => this.chartInstance?.reflow(), 500),
        });

        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "frustrationView",
            getStateData: () => this.selectedView,
            onStateLoaded: stateData => {
                if (stateData) {
                    this.selectedView = stateData;
                }
                this._viewStateLoaded = true;
            },
            autoLoad: true
        });
    }

    private createViewButtons(): void {
        const opt_Categories: ToggleButtonObjectType = {
            id: `frustration_${this.moduleKey}_categories`,
            name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'View_Button_Categories', 'Categories'),
        };
        const opt_Agents: ToggleButtonObjectType = {
            id: `frustration_${this.moduleKey}_agents`,
            name: this.cvLocalizationService.getCachedText('Frustration_Widget', 'View_Button_Agents', 'Agents'),
        };

        this._viewButtons = [opt_Categories, opt_Agents];
    }
}
